import React, { useRef, useState } from "react";
import { Box, Button, Text, Badge, Checkbox, Input, HStack, SimpleGrid, useToast, Link } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";

/**
 * Imports -> PNP -> Orders (masters only). One PnP_Orders_*.csv at a time,
 * validate-then-commit against POST /orders/upload.
 *
 * The file's VENDOR CODE decides the supplier (supplier_vendorcodes), and that
 * supplier's organisation must be the one the master has toggled — a Gateway
 * file cannot be imported while Spear Management is selected. Re-uploading a
 * file is safe: quantities/dates refresh, approvals and comments are kept.
 * docs/michael/pnp-orders/TASK-orders.md
 */

const VERDICT_COLORS = { PASS: "green", WARN: "orange", FAIL: "red" };
const LEVEL_COLORS = { error: "red", warning: "orange", info: "blue" };

const exampleText = (ex) =>
  Object.entries(ex)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}=${v}`)
    .join(", ");

const money = (n) => (n == null ? "-" : `R ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const OrdersUpload = ({ supplier }) => {
  const { selectedOrg } = useSupplierRetailer();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // validate / commit response
  const [acceptWarnings, setAcceptWarnings] = useState(false);
  const fileInput = useRef();
  const toast = useToast();

  const call = async (fd) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/upload`, { method: "POST", body: fd });
    return { httpStatus: res.status, body: await res.json() };
  };

  const base = () => {
    const fd = new FormData();
    fd.append("supplier", supplier || "");
    fd.append("org_code", selectedOrg || "");
    return fd;
  };

  const pick = (e) => {
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
    setResult(null);
  };

  const validate = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = base();
      fd.append("csvFile", file);
      fd.append("mode", "validate");
      const { body } = await call(fd);
      setResult(body);
      if (!body.verdict) toast({ title: "Validation failed", description: body.message, status: "error", duration: 6000, isClosable: true });
    } catch (err) {
      toast({ title: "Validation failed", description: String(err.message || err), status: "error", duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!result || !result.fileKey) return;
    setBusy(true);
    try {
      const fd = base();
      fd.append("mode", "commit");
      fd.append("fileKey", result.fileKey);
      fd.append("acceptWarnings", acceptWarnings ? "true" : "false");
      const { body } = await call(fd);
      setResult(body);
      toast({ title: body.status === 1 ? "Orders imported" : "Import failed", description: body.message, status: body.status === 1 ? "success" : "error", duration: 7000, isClosable: true });
    } catch (err) {
      toast({ title: "Import failed", description: String(err.message || err), status: "error", duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const s = result && result.summary;
  const importable = result && result.verdict && !result.import && (result.verdict === "PASS" || (result.verdict === "WARN" && acceptWarnings));

  return (
    <Box>
      <Text fontSize="sm" color="gray.500" mb={2}>
        Select one PnP orders file (<b>PnP_Orders_…_SelectedList_….csv</b>). The file's <b>vendor code</b> decides the supplier, and that
        supplier must belong to the organisation you have selected ({selectedOrg || "any"}). Step 1 validates (nothing is written), step 2
        imports. Re-uploading is safe — approvals and comments are kept. The orders then show on the <b>Orders</b> page.
      </Text>
      <HStack mb={3} spacing={3} flexWrap="wrap">
        <Input type="file" accept=".csv" ref={fileInput} onChange={pick} maxW="360px" p={1} />
        <Button colorScheme="blue" onClick={validate} isDisabled={!file || busy} isLoading={busy && !result}>
          1. Validate
        </Button>
        <Button colorScheme="green" onClick={commit} isDisabled={!importable || busy} isLoading={busy && !!result}>
          2. Import
        </Button>
        {result && result.verdict === "WARN" && !result.import && (
          <Checkbox isChecked={acceptWarnings} onChange={(e) => setAcceptWarnings(e.target.checked)}>
            import despite warnings
          </Checkbox>
        )}
      </HStack>

      {result && result.verdict && (
        <Box borderWidth="1px" borderRadius="md" p={3} mb={3}>
          <HStack mb={2} spacing={3}>
            <Badge colorScheme={result.import ? "green" : VERDICT_COLORS[result.verdict] || "gray"} fontSize="sm">
              {result.import ? "IMPORTED" : result.verdict}
            </Badge>
            <Text fontSize="sm" fontWeight="semibold">{result.fileName}</Text>
            {result.import && (
              <Text fontSize="sm">
                {result.import.inserted} new · {result.import.updated} updated
                {result.import.unmatched ? ` · ${result.import.unmatched} unmatched vendor line(s)` : ""} ({(result.import.ms / 1000).toFixed(1)}s) —{" "}
                <Link as={RouterLink} to={`/orders?file=${result.import.fileId}${result.import.suppliers && result.import.suppliers[0] ? `&supplier=${encodeURIComponent(result.import.suppliers[0])}` : ""}`} color="blue.500" fontWeight="semibold">open these orders on the Orders page</Link>
              </Text>
            )}
          </HStack>
          {s && (
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} fontSize="xs" mb={2}>
              <Box><b>Supplier(s)</b><br />{(s.suppliers || []).map((x) => `${x.supplier} (${x.org || "no org"}) ${x.lines}`).join(" · ") || "-"}{s.unknownVendors && s.unknownVendors.length ? ` · unmapped vendor ${s.unknownVendors.join(", ")}` : ""}</Box>
              <Box><b>Lines / POs / stores / products</b><br />{s.lines} / {s.pos} / {s.stores} / {s.products}</Box>
              <Box><b>Cases / units / value</b><br />{s.cases} / {s.units} / {money(s.value)}</Box>
              <Box><b>PO types</b><br />{Object.entries(s.poTypes || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}</Box>
              <Box><b>Order dates</b><br />{s.orderDateFrom}{s.orderDateTo !== s.orderDateFrom ? ` → ${s.orderDateTo}` : ""}</Box>
              <Box><b>Delivery dates</b><br />{s.deliveryFrom || "-"}{s.deliveryTo && s.deliveryTo !== s.deliveryFrom ? ` → ${s.deliveryTo}` : ""}</Box>
              <Box><b>PnP article matched</b><br />{s.articleViaKey + s.articleViaDescription} of {s.lines} ({s.articleViaKey} by barcode/code, {s.articleViaDescription} by description, {s.articleUnresolved} new)</Box>
              <Box><b>With an SOH row / already imported</b><br />{s.sdcCovered} / {s.alreadyImported}</Box>
            </SimpleGrid>
          )}
          {result.message && !result.import && (
            <Text fontSize="xs" fontWeight="bold" color={result.verdict === "FAIL" ? "red.600" : "gray.700"} mb={1}>{result.message}</Text>
          )}
          {(result.issues || []).map((i, idx) => (
            <Box key={idx} mb={1}>
              <HStack spacing={2} align="baseline">
                <Badge colorScheme={LEVEL_COLORS[i.level] || "gray"}>{i.level}</Badge>
                <Text fontSize="xs" fontWeight="semibold">{i.message}{i.count > 1 && !/^\d/.test(i.message) ? ` (${i.count})` : ""}</Text>
              </HStack>
              {i.examples && i.examples.length > 0 && (
                <Text fontSize="xs" color="gray.600" ml={1}>
                  e.g. {i.examples.map(exampleText).join("  •  ")}{i.count > i.examples.length ? `  … and ${i.count - i.examples.length} more` : ""}
                </Text>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OrdersUpload;
